import { useEffect, useState, useRef, useCallback } from 'react'
import { WorkBreakdowns } from '../../types/Project/WorkBreakdowns'
import { WorkItemMetadata } from '../../types/Project/WorkItemMetadata'
import { Spec } from '../../types/Project/Spec'
import { ScaffoldRequestForm } from '../../types/Scaffold/ScaffoldRequest'
import { ScaffoldWorkItem, WorkItemTemplate } from '../../types/Scaffold/Scaffold'
import { getProjectWBS, getWorkItemParents } from '../../services/scaffoldRequestService'
import { getProjectSpecs, getProjectWorkItemMetadata } from '../../services/projectService'
import { createWorkItemTemplate } from '../../utils/ScaffoldWorkItemUtils'

interface UseWorkItemBuilderOptions {
	scaffoldRequest: ScaffoldRequestForm | null
	addChildtoId?: number
	onHierarchyBuilt?: () => void
}

interface UseWorkItemBuilderReturn {
	// Data
	wbsData: WorkBreakdowns[]
	metadataTypes: WorkItemMetadata[]
	specs: Spec[]
	isLoading: boolean
	
	// Initialization
	initializeHierarchy: (
		selectedWBS: WorkBreakdowns,
		unitOfMeasure: string,
		existingHierarchy?: ScaffoldWorkItem[],
		incrementId?: () => number,
	) => WorkItemTemplate[]
	
	// Existing hierarchy data
	existingHierarchy: ScaffoldWorkItem[]
	existingHierarchyLoading: boolean
}

/**
 * Custom hook that handles data fetching and business logic for work item builder.
 */
export function useWorkItemBuilder({
	scaffoldRequest,
	addChildtoId,
	onHierarchyBuilt,
}: UseWorkItemBuilderOptions): UseWorkItemBuilderReturn {
	const [wbsData, setWbsData] = useState<WorkBreakdowns[]>([])
	const [metadataTypes, setMetadataTypes] = useState<WorkItemMetadata[]>([])
	const [specs, setSpecs] = useState<Spec[]>([])
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [existingHierarchy, setExistingHierarchy] = useState<ScaffoldWorkItem[]>([])
	const [existingHierarchyLoading, setExistingHierarchyLoading] = useState<boolean>(false)

	// Fetch initial data (WBS, metadata, specs)
	useEffect(() => {
		setIsLoading(true)
		Promise.all([getProjectWBS(), getProjectWorkItemMetadata(), getProjectSpecs()])
			.then(([wbsData, metadataTypes, specs]) => {
				setWbsData(wbsData)
				setMetadataTypes(metadataTypes)
				setSpecs(specs)
			})
			.finally(() => {
				setIsLoading(false)
			})
	}, [])

	// Fetch existing hierarchy when adding a child
	useEffect(() => {
		if (addChildtoId && wbsData.length > 0) {
			setExistingHierarchyLoading(true)
			getWorkItemParents(addChildtoId)
				.then((parents) => {
					setExistingHierarchy(parents)
				})
				.finally(() => {
					setExistingHierarchyLoading(false)
				})
		} else {
			setExistingHierarchy([])
		}
	}, [addChildtoId, wbsData])

	/**
	 * Updates templates with data from existing hierarchy
	 */
	const updateTemplatesFromExistingHierarchy = (
		templates: WorkItemTemplate[],
		existingItems: ScaffoldWorkItem[],
	): WorkItemTemplate[] => {
		return templates.map((template) => {
			// Find matching item from existing hierarchy by workItemTypeId
			const matchingItem = existingItems.find(
				(item) => item.workItemTypeId === template.wbsWorkItemType.id,
			)

			// Update template with data from existing item if found
			const updatedTemplate: WorkItemTemplate = matchingItem
				? {
						...template,
						quantityBudgeted: matchingItem.quantityBudgeted,
						budgetedHours: matchingItem.budgetedHours,
						releaseDate: matchingItem.releaseDate ?? template.releaseDate,
						nocDate: matchingItem.nocDate ?? template.nocDate,
						unitOfMeasure: matchingItem.unitOfMeasure || template.unitOfMeasure,
						name: matchingItem.name || template.name,
						// Store original values for parents so we can add children values to them
						originalQuantityBudgeted: matchingItem.quantityBudgeted,
						originalBudgetedHours: matchingItem.budgetedHours,
				  }
				: template

			// Recursively update children
			if (updatedTemplate.children && updatedTemplate.children.length > 0) {
				return {
					...updatedTemplate,
					children: updateTemplatesFromExistingHierarchy(
						updatedTemplate.children,
						existingItems,
					),
				}
			}

			return updatedTemplate
		})
	}

	/**
	 * Initializes the work item hierarchy based on selected WBS and unit of measure.
	 * If existing hierarchy is provided, it will populate the templates with existing data.
	 * incrementId function should be provided from the context to ensure unique IDs.
	 */
	const initializeHierarchy = useCallback((
		selectedWBS: WorkBreakdowns,
		unitOfMeasure: string,
		existingHierarchyData?: ScaffoldWorkItem[],
		incrementId?: () => number,
	): WorkItemTemplate[] => {
		// If no incrementId provided, create a temporary one (shouldn't happen in normal flow)
		let localCounter = 0
		const localIncrementId = incrementId || (() => localCounter++)

		// Create hierarchical structure based on work item type index
		const template = createWorkItemTemplate(
			0, // Not used, incrementId handles the counter
			localIncrementId,
			selectedWBS,
			selectedWBS.workItemTypes.sort((a, b) => a.index - b.index)[0],
			undefined,
			1,
			scaffoldRequest || undefined,
			unitOfMeasure,
			scaffoldRequest?.po ?? undefined,
			scaffoldRequest?.workOrder ?? undefined,
		)

		// If there's existing hierarchy data, update templates with it
		const templates =
			existingHierarchyData && existingHierarchyData.length > 0
				? updateTemplatesFromExistingHierarchy([template], existingHierarchyData)
				: [template]

		// Notify that hierarchy is built
		if (onHierarchyBuilt) {
			onHierarchyBuilt()
		}

		return templates
	}, [scaffoldRequest, onHierarchyBuilt])

	return {
		wbsData,
		metadataTypes,
		specs,
		isLoading,
		initializeHierarchy,
		existingHierarchy,
		existingHierarchyLoading,
	}
}
